import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { apiClient } from '../../services/apiClient';

const RFPChatPanel = ({ researchId }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    loadChatHistory();
  }, [researchId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.getRFPChatMessages(researchId);
      if (response.success) {
        setMessages(response.data.messages || []);
      }
    } catch (err) {
      console.error('Failed to load chat history:', err);
      setError('Failed to load chat history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isStreaming) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setError('');

    // Add user message to display
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, newUserMessage]);
    setIsStreaming(true);
    setStreamingMessage('');

    let fullResponse = '';

    try {
      await apiClient.sendRFPChatMessage(
        researchId,
        userMessage,
        (chunk) => {
          // On chunk received
          fullResponse += chunk;
          setStreamingMessage(fullResponse);
        },
        () => {
          // On complete
          const assistantMessage = {
            role: 'assistant',
            content: fullResponse,
            created_at: new Date().toISOString()
          };

          setMessages(prev => [...prev, assistantMessage]);
          setStreamingMessage('');
          setIsStreaming(false);
        },
        (error) => {
          // On error
          setError(`Error: ${error}`);
          setIsStreaming(false);
          setStreamingMessage('');
        }
      );
    } catch (err) {
      setError('Failed to send message');
      setIsStreaming(false);
      setStreamingMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = async () => {
    if (!window.confirm('Are you sure you want to clear the chat history? This cannot be undone.')) {
      return;
    }

    try {
      await apiClient.clearRFPChatHistory(researchId);
      setMessages([]);
      setStreamingMessage('');
      setError('');
    } catch (err) {
      setError('Failed to clear chat history');
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-sm border border-slate-200">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-slate-200">
        <div className="flex items-center space-x-2">
          <FontAwesomeIcon icon="comments" className="text-primary-600" />
          <h3 className="font-semibold text-slate-900 text-sm">RFP Analysis Chat</h3>
        </div>
        {messages.length > 0 && (
          <button
            onClick={handleClearChat}
            className="text-slate-400 hover:text-red-600 text-xs"
            title="Clear chat history"
          >
            <FontAwesomeIcon icon="trash" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <FontAwesomeIcon icon="spinner" className="fa-spin text-slate-400" />
          </div>
        ) : messages.length === 0 && !streamingMessage ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-slate-500 text-sm">
            <FontAwesomeIcon icon="comments" className="text-4xl mb-3 text-slate-300" />
            <p>Ask questions about this RFP</p>
            <p className="text-xs mt-1">e.g., "Does this RFP have SOC 2 requirements?"</p>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                    msg.role === 'user'
                      ? 'bg-primary-600 text-white'
                      : 'bg-slate-100 text-slate-900'
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                </div>
              </div>
            ))}

            {/* Streaming message */}
            {isStreaming && streamingMessage && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-slate-100 text-slate-900">
                  <div className="whitespace-pre-wrap break-words">{streamingMessage}</div>
                  <FontAwesomeIcon icon="spinner" className="fa-spin ml-2 text-xs" />
                </div>
              </div>
            )}

            {/* Streaming placeholder */}
            {isStreaming && !streamingMessage && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-slate-100 text-slate-900">
                  <FontAwesomeIcon icon="spinner" className="fa-spin" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="px-3 py-2 bg-red-50 border-t border-red-200 text-red-700 text-xs">
          {error}
        </div>
      )}

      {/* Input */}
      <div className="p-3 border-t border-slate-200">
        <div className="flex space-x-2">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask a question about this RFP..."
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 resize-none"
            rows="2"
            disabled={isStreaming}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isStreaming}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            <FontAwesomeIcon icon={isStreaming ? 'spinner' : 'paper-plane'} className={isStreaming ? 'fa-spin' : ''} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RFPChatPanel;
